import { api } from "@/api/axios.ts";
import {
  ApiEmployeeChartFilter,
  ApiEmployeeChartItem,
  EmployeeNodeMode,
} from "@/pages/employees/chart/employees-chart.types.ts";
import {
  ApiEmployeeHireJobQueued,
  ApiEmployeeHireJobStatus,
  ApiEmployeeHirePayload,
  PrefillCandidate,
} from "@/types/employees/employee-hire.types";
import { ApiPhotoUpdate } from "@/types/comment.types.ts";
import { ApiEmployeeColumn } from "@/types/employees/employee-column.types.ts";
import { ApiOperationResult } from "@/types/common.types.ts";
import {
  ApiEmployeeAssignPositionPayload,
  ApiEmployeePositionPagedData,
} from "@/types/employees/employee-position.types.ts";
import {
  ApiAuthorization,
  ApiEmployee,
  ApiEmployeeAccountSettingsSavePayload,
  ApiEmployeeBanPayload,
  ApiEmployeeEditPayload,
  ApiEmployeeFilter,
  ApiEmployeeInviteLink,
  ApiEmployeeWithRoles,
  ApiImpersonationTokenCreated,
  ApiMeEmployee,
  ApiTwoFactorSetup,
  ApiTwoFactorSetupVerifyPayload,
  ChangePasswordSource,
} from "@/types/employees/employee.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { ApiHiringPlanFilter, ApiPagedHiringPlanData } from "@/types/recruit/hiring-plan.types.ts";
import { ApiInviteEmailType } from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiEmployeeLoginMethods,
  ApiInviteLoginMethods,
  ApiLoginOrganization,
} from "@/types/security.types.ts";

export const apiEmployees = {
  search: async function (filter: ApiEmployeeFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiEmployee>>("/api/v1/employees/search", filter, signal);
  },
  searchColumns: async function (signal?: AbortSignal) {
    return api.get<ApiEmployeeColumn[]>("/api/v1/employees/search/columns", {}, signal);
  },
  chart: async function (
    employee_id: number | undefined,
    mode: EmployeeNodeMode = EmployeeNodeMode.children,
    filter: ApiEmployeeChartFilter
  ) {
    return api.post<ApiEmployeeChartItem[]>("/api/v1/employees/chart", {
      ...filter,
      employee_id: employee_id,
      mode: mode,
    });
  },
  searchWithRoles: async function (filter: ApiEmployeeFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiEmployeeWithRoles>>(
      `/api/v1/roles/employees-roles`,
      filter,
      signal
    );
  },
  getById: async function (id: number | string) {
    return api.get<ApiEmployee>(`/api/v1/employees/${id}`);
  },
  getByListOfId: async function (ids: number[]) {
    return api.post<ApiEmployee[]>(`/api/v1/employees/get-by-ids`, {
      employee_ids: ids,
    });
  },
  update: async function (id: number, payload: ApiEmployeeEditPayload) {
    return api.put<ApiEmployee>(`/api/v1/employees/${id}`, payload);
  },
  setAvatar: async function (employeeId: number, fileUUID: string) {
    return api.put<ApiPhotoUpdate>(`/api/v1/employees/${employeeId}/avatar`, {
      file_uuid: fileUUID,
    });
  },
  deleteAvatar: async function (employeeId: number) {
    return api.delete<ApiPhotoUpdate>(`/api/v1/employees/${employeeId}/avatar`);
  },
  setBackground: async function (employeeId: number, fileUUID: string) {
    return api.put<ApiPhotoUpdate>(`/api/v1/employees/${employeeId}/background`, {
      file_uuid: fileUUID,
    });
  },
  deleteBackground: async function (employeeId: number) {
    return api.delete<ApiPhotoUpdate>(`/api/v1/employees/${employeeId}/background`);
  },
  getMe: async function () {
    return api.get<ApiMeEmployee>("/api/v1/employees/me");
  },
  updateAccountSettings: async function (payload: ApiEmployeeAccountSettingsSavePayload) {
    return api.put<ApiMeEmployee>("/api/v1/employees/me", payload);
  },

  resetConfirmEmail: async function () {
    return api.post("/api/v1/employees/resend-confirm-email", {});
  },
  positionCreate: async function (id: number, payload: ApiEmployeeAssignPositionPayload) {
    return api.post<ApiEmployee>(`/api/v1/employees/${id}/positions`, payload);
  },
  positionUpdate: async function (
    id: number,
    positionId: number,
    payload: ApiEmployeeAssignPositionPayload
  ) {
    return api.put<ApiEmployee>(`/api/v1/employees/${id}/positions/${positionId}`, payload);
  },
  positionDelete: async function (employeeId: number, employeePositionId: number) {
    return api.delete(`/api/v1/employees/${employeeId}/positions/${employeePositionId}`);
  },
  positionSearch: async function (employeeId: number, pageNumber: number) {
    return api.post<ApiEmployeePositionPagedData>(
      `/api/v1/employees/${employeeId}/positions/search`,
      {
        page_number: pageNumber,
      }
    );
  },
  login: async function (email: string, password: string) {
    const formData = new FormData();
    formData.append("username", email);
    formData.append("password", password);

    return api.post<ApiAuthorization>("/api/v1/employees/login", formData);
  },
  getLoginMethods: async function (email: string) {
    return api.post<ApiEmployeeLoginMethods>("/api/v1/employees/login/methods", { email });
  },
  getLoginOrganizations: async function (pendingToken: string) {
    return api.get<ApiLoginOrganization[]>("/api/v1/employees/login/organizations", {}, undefined, {
      headers: { Authorization: `Bearer ${pendingToken}` },
    });
  },
  selectLoginOrganization: async function (pendingToken: string, organizationId: number) {
    return api.post<ApiAuthorization>("/api/v1/employees/login/organization", {
      access_token: pendingToken,
      organization_id: organizationId,
    });
  },
  forgotPassword: async function (email: string) {
    return api.post("/api/v1/employees/forgot-password", { email });
  },
  resetPassword: async function (token: string) {
    return api.post<ApiAuthorization>("/api/v1/employees/reset-password", { token });
  },
  getInviteLoginMethods: async function (token: string, signal?: AbortSignal) {
    return api.post<ApiInviteLoginMethods>(
      "/api/v1/employees/invite/login-methods",
      { token },
      signal
    );
  },
  requestInviteCode: async function (token: string) {
    return api.post<ApiOperationResult>("/api/v1/employees/invite-code/request", { token });
  },
  verifyInviteCode: async function (token: string, code: string) {
    return api.post<ApiAuthorization>("/api/v1/employees/invite-code/verify", { token, code });
  },
  changePassword: async function (
    password: string,
    source: ChangePasswordSource,
    accessToken?: string
  ) {
    return api.post<ApiAuthorization>(
      "/api/v1/employees/change-password",
      { password, source },
      undefined,
      accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined
    );
  },
  resentInviteEmail: async function (employeeId: number, inviteEmailType: ApiInviteEmailType) {
    return api.post(`/api/v1/employees/${employeeId}/resend-invite-email`, {
      invite_email_type: inviteEmailType,
    });
  },
  createInviteLink: async function (employeeId: number, inviteEmailType: ApiInviteEmailType) {
    return api.post<ApiEmployeeInviteLink>(`/api/v1/employees/${employeeId}/create-invite-link`, {
      invite_email_type: inviteEmailType,
    });
  },
  export: function (filter: ApiEmployeeFilter) {
    return api.post<BlobPart>(`/api/v1/employees/export`, filter, undefined, {
      responseType: "blob",
    });
  },
  hiringPlanSearch: async function (
    employeeId: number,
    filter: ApiHiringPlanFilter,
    signal?: AbortSignal
  ) {
    return api.post<ApiPagedHiringPlanData>(
      `/api/v1/employees/${employeeId}/hiring-plans/search`,
      filter,
      signal
    );
  },
  hire: async function (payload: ApiEmployeeHirePayload) {
    return api.post<ApiEmployeeHireJobQueued>("/api/v1/employees/hire", payload);
  },
  hireJobStatus: async function (jobId: number, accessKey: string) {
    return api.get<ApiEmployeeHireJobStatus>(`/api/v1/employees/hire-jobs/${jobId}`, {
      access_key: accessKey,
    });
  },
  prefillCandidate: async function (candidateId: number) {
    return api.get<PrefillCandidate>(`/api/v1/employees/setup/prefill/${candidateId}`);
  },
  twoFactorSetup: async function () {
    return api.post<ApiTwoFactorSetup>(`/api/v1/employees/tfa/setup`, {});
  },
  twoFactorSetupVerify: async function (payload: ApiTwoFactorSetupVerifyPayload) {
    return api.post<ApiAuthorization>(`/api/v1/employees/tfa/setup-verify`, payload);
  },
  twoFactorDisable: async function (payload: ApiTwoFactorSetupVerifyPayload) {
    return api.post<ApiOperationResult>(`/api/v1/employees/tfa/disable`, payload);
  },
  twoFactorDisableForce: async function (employeeId: number) {
    return api.post<ApiOperationResult>(`/api/v1/employees/tfa/${employeeId}/disable-force`, {});
  },
  twoFactorVerify: async function (access_token: string, code: string) {
    return api.post<ApiAuthorization>(`/api/v1/employees/tfa/verify`, { access_token, code });
  },
  delete: async function (employeeId: number, successorEmployeeId: number) {
    return api.delete<ApiOperationResult>(
      `/api/v1/employees/${employeeId}?successor_employee_id=${successorEmployeeId}`
    );
  },
  updateBan: async function (employeeId: number, payload: ApiEmployeeBanPayload) {
    return api.put<ApiOperationResult>(`/api/v1/employees/${employeeId}/ban`, payload);
  },
  impersonationStart: async function (employeeId: number, reason?: string) {
    return api.post<ApiImpersonationTokenCreated>(`/api/v1/impersonation/employees/${employeeId}`, {
      reason: reason ?? null,
    });
  },
  impersonationEnd: async function () {
    return api.delete<ApiOperationResult>("/api/v1/impersonation/current");
  },
};
